import { useCallback, useContext } from 'react';
import { CombinePortfoliosWizardContext } from './CombinePortfoliosWizardContext';
import { type AnalysisViewSubjectGroup, createPortfolioV3, getSpecificAnalysisView } from 'venn-api';
import { useRecoilValue } from 'recoil';
import { proxySetIdState, type Subject } from 'venn-state';
import {
  convertTemplateToView,
  getRandomId,
  getSubjects,
  MAX_SUBJECT_GROUP_SIZE,
  navigateToReportLab,
} from 'venn-utils';
import { useHistory } from 'react-router-dom';
import { Notifications, NotificationType } from 'venn-ui-kit';
import { cloneDeep } from 'lodash';

const addSubjectsToSubjectGroups = (
  subjectIds: Subject[],
  existingSubjectGroups: AnalysisViewSubjectGroup[] | undefined,
): AnalysisViewSubjectGroup[] => {
  const subjectGroupToAddTo = existingSubjectGroups?.findIndex(
    (group) => (group.subjects?.length ?? 0) + subjectIds.length <= MAX_SUBJECT_GROUP_SIZE,
  );

  if (
    !existingSubjectGroups ||
    subjectGroupToAddTo === undefined ||
    subjectGroupToAddTo < 0 ||
    !existingSubjectGroups[subjectGroupToAddTo]
  ) {
    return [
      ...(existingSubjectGroups ?? []),
      {
        id: getRandomId(),
        label: 'Portfolio Selection',
        subjects: getSubjects(subjectIds),
      },
    ];
  }

  const groups = cloneDeep(existingSubjectGroups);
  groups[subjectGroupToAddTo]!.subjects = [
    ...(groups[subjectGroupToAddTo]!.subjects ?? []),
    ...getSubjects(subjectIds),
  ];
  return groups;
};

export const useNavigateToView = (onClose: () => void) => {
  const { ownershipSettings, templateSettings, portfolios } = useContext(CombinePortfoliosWizardContext);
  const proxySetId = useRecoilValue(proxySetIdState);
  const history = useHistory();

  return useCallback(async () => {
    if (!portfolios || !templateSettings.templateId) {
      return;
    }

    try {
      const portfolioIdsPromise = Promise.all(
        portfolios.map((portfolio) => {
          if (portfolio.id) {
            return Promise.resolve(portfolio.id);
          }
          return createPortfolioV3(proxySetId, {
            ...portfolio,
            draft: true,
            ownerContextId: ownershipSettings[portfolio.name],
          }).then((res) => res.content.id);
        }),
      );
      const [template, subjectIds] = await Promise.all([
        getSpecificAnalysisView(templateSettings.templateId).then((res) => res.content),
        portfolioIdsPromise.then((portfolioIds) => portfolioIds.map((portfolioId) => ({ portfolioId }))),
      ]);

      navigateToReportLab(history, {
        newDocument: {
          ...convertTemplateToView(template),
          name: templateSettings.templateName ?? undefined,
          subjectGroups: addSubjectsToSubjectGroups(subjectIds, template.subjectGroups),
        },
      });
    } catch (e) {
      Notifications.notify('An error occurred. Please try again.', NotificationType.ERROR);
      onClose();
    }
  }, [portfolios, proxySetId, ownershipSettings, templateSettings, history, onClose]);
};
